import type {
  CohortRequest,
  OrgValueCaseResponse,
  OrgValueCohortResponse,
  OrgValuePersonResponse,
  OrgValueResponse,
} from "../value/types";
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

import { refreshWithCognito, type RefreshResult } from "../auth/cognitoRefresh";
import { clearSession, getAuthorizationHeaderValue } from "../auth/session";
import { dispatch } from "../store";

const API_URL = `${import.meta.env.VITE_BACKEND_URL}` || "";
const DEFAULT_TIMEOUT = 120000;

const nonTokenUrls: string[] = [
  "/microsoft_login",
  "/microsoft_login_exchange",
];

/** What a call rejects with when the network, not the server, failed. */
export const NETWORK_ERROR_MESSAGE =
  "Encountered a network issue, please check your internet connection!";

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

const instance = axios.create({
  baseURL: API_URL,
  timeout: DEFAULT_TIMEOUT,
});

let refreshInFlight: Promise<RefreshResult> | null = null;

/** One Cognito refresh at a time; concurrent 401s share its answer. */
async function refreshSessionOnce(): Promise<RefreshResult> {
  if (!refreshInFlight) {
    refreshInFlight = refreshWithCognito().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

instance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = getAuthorizationHeaderValue();

      if (token) {
        config.headers["Authorization"] = token;
      }

      if (!token && !nonTokenUrls.includes(config.url || "")) {
        return Promise.reject({
          code: "INVALID_TOKEN",
          message: "Authentication token is missing.",
        });
      }

      return config;
    } catch (error) {
      return Promise.reject(error);
    }
  },
  (error) => Promise.reject(error),
);

/**
 * How a failed call is answered. Only three things sign the user out: Cognito
 * refusing to renew the session after a 401, a 401 `reauth_required`, and a call
 * made with no token at all. Everything else rejects with something
 * `apiErrorMessage` can show:
 *
 * - 401 `reauth_required` → sign out at once, no refresh: the calendar
 *   credential needs a real sign-in, which a Cognito refresh cannot give.
 * - 401 → refresh once and retry. A refresh that could not reach Cognito keeps
 *   the session (the next call tries again) and rejects with the network
 *   message. A 401 on the retried call, with a token Cognito has just issued,
 *   still signs out: nothing on this side can fix it.
 * - 403 `seat_required` → the seat block screen, never a sign-out: the user is
 *   signed in, their org just has not given them a seat.
 * - Any other 403 (a staff or impersonation refusal, an API Gateway or WAF
 *   refusal) → the response body. It refuses one action, not the session;
 *   signing out on it bounced validly signed-in users to /login.
 * - Any other status (the 409 and 422 refusals the settings and demo flows
 *   read, a 5xx) → the response body, untouched.
 * - No response → the network message.
 */
instance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const status = error.response?.status;

    // The backend says an interactive sign-in is needed to renew the user's
    // Google/Microsoft calendar credential. Cognito would happily refresh the
    // session, so refresh-and-retry would only loop; sign out instead.
    if (
      status === 401 &&
      (error.response?.data as { error?: string } | undefined)?.error ===
        "reauth_required"
    ) {
      clearSession();
      dispatch.auth.setUserAuthenticated(false);
      return Promise.reject(error.response?.data);
    }

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      const refreshed = await refreshSessionOnce();
      if (refreshed.kind === "ok") {
        const token = getAuthorizationHeaderValue();
        if (token) {
          const headers = AxiosHeaders.from(original.headers ?? {});
          headers.set("Authorization", token);
          original.headers = headers;
        }
        return instance(original);
      }
      if (refreshed.kind === "network") {
        return Promise.reject(NETWORK_ERROR_MESSAGE);
      }
      clearSession();
      dispatch.auth.setUserAuthenticated(false);
      return Promise.reject(
        error.response?.data || { message: "Unauthorized" },
      );
    }

    if (
      status === 403 &&
      (error.response?.data as { error?: string } | undefined)?.error ===
        "seat_required"
    ) {
      dispatch.user.setSeatRevoked();
      return Promise.reject(error.response?.data);
    }

    if (status === 403) {
      return Promise.reject(error.response?.data || { message: "Forbidden" });
    }

    if (status === 401 || error.code === "INVALID_TOKEN") {
      dispatch.auth.setUserAuthenticated(false);
      return Promise.reject(
        error.response?.data || { message: "Unauthorized" },
      );
    }

    if (error.response) {
      return Promise.reject(error.response.data);
    }

    if (error.request) {
      console.error("Network issue encountered");
      return Promise.reject(NETWORK_ERROR_MESSAGE);
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------------
// Org settings page (customer self-service) + admin panel
// ---------------------------------------------------------------------------

export interface OrgMember {
  email: string;
  name: string | null;
  is_admin: boolean;
  // Whether this member holds one of the org's paid seats — what the admin
  // ticks, and what entitles them to use Dooglie at all, on every org.
  has_seat: boolean;
  // False for a colleague CRM sync provisioned, or an invite not yet taken
  // up. Optional so a backend that predates it reads as signed in.
  signed_in?: boolean;
  // When they last used Dooglie from any client (null: not since the backend
  // began recording it), and when they last started a recording (null: never).
  // Optional: the staff admin panel's roster does not send them.
  last_active_at?: string | null;
  last_call_at?: string | null;
  // The member's Dooglie user id, so staff can act as them from the admin
  // panel. Optional: the org settings page's roster does not send it.
  user_id?: string;
}

export interface OrgSettings {
  is_org_admin: boolean;
  profile: { email: string; name: string | null };
  org: { name: string; timezone: string | null; company_domain: string };
  // "used" = members holding a seat; membership (CRM-synced or invited) is free.
  seats?: {
    used: number;
    members_total: number;
    paid: number;
    contracted: number;
    over_seat: boolean;
  };
  billing?: {
    price_per_user: number;
    currency: string;
    is_monthly: boolean;
    subscription_status: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    trial_end_at: string | null;
  };
  members?: OrgMember[];
}

export const getOrgSettings = async (): Promise<OrgSettings> => {
  const response = await instance.post<OrgSettings>("/get_org_settings", {});
  return response.data;
};

// Org self-service mutations (own org only, org-admin gated server-side).
export type ManageOrgBody =
  | { action: "add_user"; email: string; first_name?: string; last_name?: string }
  | { action: "set_user_admin"; email: string; is_admin: boolean }
  // Declarative: exactly these emails hold a seat afterwards. Rejected with
  // SeatIncreaseRequired when it would grow past the paid cap, until the admin
  // confirms and re-sends with approve_increase.
  | { action: "set_seats"; emails: string[]; approve_increase?: boolean }
  | { action: "purchase_seats"; count: number }
  | { action: "reduce_seats"; count: number }
  | { action: "set_timezone"; timezone: string }
  | { action: "billing_portal" };

/** What the server needs the admin to confirm before it will raise their bill.
 *  Deliberately just the count — Stripe's invoice is the authority on price, and
 *  quoting it here would mean keeping two sources of truth in step. */
export interface SeatIncrease {
  additional: number;
}

/** Thrown by `manageOrg` when set_seats would grow past the paid cap. Carries
 *  what the confirmation dialog quotes; re-send with approve_increase to buy. */
export class SeatIncreaseRequired extends Error {
  constructor(public readonly increase: SeatIncrease) {
    super("seat_increase_required");
    this.name = "SeatIncreaseRequired";
  }
}

export const manageOrg = async (
  body: ManageOrgBody,
): Promise<{ ok?: boolean; portal_url?: string }> => {
  try {
    const response = await instance.post("/manage_org", body);
    return response.data;
  } catch (error) {
    // The response interceptor rejects with the response body itself, so the
    // 409 arrives here as the plain payload rather than an AxiosError.
    const data = error as (SeatIncrease & { error?: string }) | undefined;
    if (data?.error === "seat_increase_required") {
      throw new SeatIncreaseRequired(data);
    }
    throw error;
  }
};

// Admin panel (Dooglie staff) — directory rows, single-org detail, and the
// unified write endpoint (profile fields + billing/ops actions).
export interface AdminOrgRow {
  org_id: string;
  name: string;
  company_domain: string;
  subscription_status: string | null;
  has_access: boolean;
  trial_end_at: string | null;
  seats_used: number; // members holding a seat — what the subscription must cover
  members_total: number;
  paid_seats: number;
  contracted_seats: number;
  over_seat: boolean;
  currency: string;
  price_per_user: number;
  is_monthly: boolean;
  crm_provider: string | null;
  created_at: number;
}

export interface AdminOrgDetail extends AdminOrgRow {
  account_notes: string | null;
  timezone: string | null;
  start_date: string | null;
  end_date: string | null;
  admin_users: string[];
  assistant_uses: number;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  members: OrgMember[];
  sync: {
    vector_store_provisioned: boolean;
  };
}

export const searchOrgs = async (query: string): Promise<AdminOrgRow[]> => {
  const response = await instance.post<AdminOrgRow[]>("/search_orgs", { query });
  return response.data;
};

export const getOrgDetail = async (org_id: string): Promise<AdminOrgDetail> => {
  const response = await instance.post<AdminOrgDetail>("/search_orgs", { org_id });
  return response.data;
};

export interface AdminUpdateOrgBody {
  org_id?: string;
  fields?: Record<string, unknown>;
  // Note: no billing_portal — the Stripe portal is org-admin-only (manageOrg),
  // never exposed to Dooglie staff (it grants unilateral billing control).
  action?: "issue_subscription" | "change_seats" | "set_trial" | "clear_trial";
  seats?: number;
  trial_days?: number;
}

export const adminUpdateOrg = async (
  body: AdminUpdateOrgBody,
): Promise<{ org_id: string; checkout_url?: string; portal_url?: string }> => {
  const response = await instance.post("/update_org", body);
  return response.data;
};

/**
 * The human-readable reason behind a failed request.
 *
 * The response interceptor rejects with the response body itself, so a refused
 * call arrives here as `{error, message}` rather than an AxiosError; a network
 * failure arrives as a plain string; anything else is an ordinary Error.
 */
export const apiErrorMessage = (
  e: unknown,
  fallback = "Something went wrong.",
): string => {
  if (typeof e === "string") return e;
  const body = e as { message?: string; error?: string } | undefined;
  return body?.message || body?.error || fallback;
};

// ---------------------------------------------------------------------------
// Prepare demo (Dooglie staff) — a prospect's own org and Cowtrumpet world,
// built from their website ahead of the meeting
// ---------------------------------------------------------------------------

/** Who the salesperson plays in the prospect's world. */
export type DemoScenario = "candidate" | "client";

export interface DemoGuest {
  email: string;
  name: string;
}

/** A recent or upcoming meeting (last day, next 7 days) with at least one guest off the
 *  salesperson's own domain. */
export interface DemoMeeting {
  id: string;
  summary: string;
  start_time: string;
  meeting_url: string | null;
  guests: DemoGuest[];
}

export interface DemoMeetings {
  meetings: DemoMeeting[];
  // "none" when the salesperson has no calendar connected.
  calendar?: "none";
}

/** What the crawl made of the prospect's website. Every field is present. */
export interface DemoBrief {
  domain: string;
  firm_name: string;
  one_line: string;
  model: string;
  industries: string[];
  specialisms: string[];
  job_titles_seen: string[];
  seniority_band: string;
  contract_types: string[];
  named_clients: string[];
  named_people: string[];
  locations: string[];
  currency: string;
  firm_size_signal: string;
  tone: string;
  evidence_urls: string[];
  confidence: "high" | "medium" | "low";
  source_chars: number;
}

export type DemoPrepareStep =
  | "none"
  | "requested"
  | "crawling"
  | "brief_ready"
  | "building_tenant"
  | "creating_org"
  | "seeding"
  | "warming"
  | "ready"
  | "failed";

export interface DemoHeroLinks {
  candidate_url: string | null;
  contact_url: string | null;
  job_url: string | null;
  company_url: string | null;
}

export interface DemoProspectProgress {
  signed_in?: boolean | null;
  extension_seen_at?: string | null;
  link_opened_at?: string | null;
}

export interface DemoPrepareStatus {
  status: DemoPrepareStep;
  step_at?: string | null;
  error?: string | null;
  prospect_email?: string | null;
  prospect_domain?: string | null;
  meeting_start?: string | null;
  scenario?: DemoScenario | null;
  brief?: DemoBrief | null;
  org_id?: string | null;
  org_name?: string | null;
  tenant_slug?: string | null;
  tenant_url?: string | null;
  hero?: DemoHeroLinks | null;
  prospect_user_id?: string | null;
  login_url?: string | null;
  expires_at?: string | null;
  // How far the prospect has got, for a ready demo; null or absent otherwise
  // (and from an older backend).
  prospect_progress?: DemoProspectProgress | null;
  // Prose items the tenant builder could not make pass its checks; the demo
  // is still ready, these are what to steer around. Empty when all passed.
  warnings?: string[];
}

export type DemoSessionBody =
  | { action: "meetings" }
  | {
      action: "prepare";
      meeting_id: string;
      prospect_email: string;
      scenario: DemoScenario;
      // The company website the demo is built from; omitted, the server uses
      // the guest's email domain.
      site?: string;
      brief_override: DemoBrief | null;
    }
  | { action: "prepare_status" }
  | { action: "retry" }
  | { action: "crm_link" }
  | { action: "teardown" };

/**
 * A one-use sign-in link into the prospect's demo CRM, minted on each
 * `crm_link` call (never kept by the server). Refused with `not_ready`
 * (409) before the demo is ready, or `crm_link_unsupported` (409) when the
 * CRM has no sign-in links.
 */
export interface DemoCrmLink {
  url: string;
  expires_at: string;
}

/**
 * A refusal from `prepare`: the prospect already has a Dooglie user, their
 * domain already has an org, or a demo is already being prepared (409s); or
 * the salesperson has no first and last name on their Dooglie user, which the
 * hero record is named after (422 `salesperson_has_no_name`); or the `site`
 * is not a website address (422 `invalid_site`), or is missing for a guest on
 * a personal mailbox (422 `site_required`). `retry` is refused with
 * `in_progress` (a step is running, or another click already restarted it),
 * `teardown_failed` (only another teardown can finish that one) or
 * `not_retryable` (no demo, a ready one, one being torn down). The response
 * interceptor hands either body back as the rejection, never a sign-out.
 */
export interface DemoPrepareRefusal {
  error:
    | "prospect_has_org"
    | "domain_has_org"
    | "in_progress"
    | "teardown_failed"
    | "not_retryable"
    | "salesperson_has_no_name"
    | "invalid_site"
    | "site_required"
    | "not_ready"
    | "crm_link_unsupported"
    | "crm_link_not_ready"
    | string;
  org_name?: string;
  message?: string;
}

/**
 * The refusal a demo call can give while Dooglie is still processing the
 * caller's last chat line: not a failure, the same call is worth repeating
 * after `retry_after_s` (see demo/processing.ts).
 */
export interface DemoProcessing {
  error: "demo_processing";
  retry_after_s?: number;
  message?: string;
}

export function demoSession(body: { action: "meetings" }): Promise<DemoMeetings>;
export function demoSession(body: { action: "prepare_status" }): Promise<DemoPrepareStatus>;
export function demoSession(body: { action: "crm_link" }): Promise<DemoCrmLink>;
/** The step the server restarted the failed demo at. */
export function demoSession(body: { action: "retry" }): Promise<{ status: DemoPrepareStep }>;
export function demoSession(
  body:
    | Extract<DemoSessionBody, { action: "prepare" }>
    | { action: "teardown" },
): Promise<{ status?: string }>;
export async function demoSession(body: DemoSessionBody) {
  const response = await instance.post("/demo_session", body);
  return response.data;
}

// ---------------------------------------------------------------------------
// Staff impersonation — a Dooglie staff member acting as another user
// ---------------------------------------------------------------------------

/** Whom the signed-in staff member is acting as (from /get_user_info). */
export interface ActingAs {
  user_id: string;
  name: string | null;
  email: string;
  org_name: string | null;
  expires_at?: string | null;
}

export type ImpersonateBody = { user_id: string } | { stop: true };

/** Starts or stops acting as another user. The server answers `reload: true`:
 *  every request after this one carries the new identity, and the realtime
 *  socket is bound to the old one, so the page reloads. */
export const impersonate = async (
  body: ImpersonateBody,
): Promise<{ acting_as: ActingAs | null; reload: boolean }> => {
  const response = await instance.post("/impersonate", body);
  return response.data;
};

export default instance;

// ---------------------------------------------------------------------------
// Value scorecard (Dooglie staff) — what Dooglie did for an org's numbers
// ---------------------------------------------------------------------------

/**
 * The org's value scorecard. Staff only: a non-staff caller is refused with
 * 403 `{error: "staff_only"}` (the request rejects with that body). With a
 * `person` (a Dooglie user id) the answer is that one person's row with their
 * outcomes, weekly line and open pairs.
 */
export function getOrgValue(orgId: string): Promise<OrgValueResponse>;
export function getOrgValue(orgId: string, person: string): Promise<OrgValuePersonResponse>;
export async function getOrgValue(
  orgId: string,
  person?: string,
): Promise<OrgValueResponse | OrgValuePersonResponse> {
  const response = await instance.post<OrgValueResponse | OrgValuePersonResponse>(
    "/get_org_value",
    person ? { org_id: orgId, person } : { org_id: orgId, version: 4 },
  );
  return response.data;
}

/**
 * What Dooglie did for one person on one outcome (staff only): their calls with
 * the candidate, the live suggestions shown on them and what the CRM sync wrote.
 */
export async function getOrgValueCase(
  orgId: string,
  person: string,
  rowKey: string,
): Promise<OrgValueCaseResponse> {
  const response = await instance.post<OrgValueCaseResponse>("/get_org_value", {
    org_id: orgId,
    person,
    case: rowKey,
    version: 4,
  });
  return response.data;
}

/** The cases behind one point of a pipeline ratio chart (staff only). */
export async function getOrgValueCohort(
  orgId: string,
  cohort: CohortRequest,
): Promise<OrgValueCohortResponse> {
  const response = await instance.post<OrgValueCohortResponse>("/get_org_value", {
    org_id: orgId,
    cohort,
    version: 4,
  });
  return response.data;
}
